import React, { useState, useMemo } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import TimePicker from "@components/ui/selection/TimePicker";
import DropDown from "@components/ui/dropdown/DropDown";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { useDispoAtelierStore, ZONE_TYPES, ZONE_COLORS, getMonday, getWeekDates } from "@store/dispoAtelierStore";
import type { AvailabilityBlock, BlockRecurrence, ZoneType } from "@store/disponibiliteStore";
import { getBlockRecurrence } from "@store/disponibiliteStore";
import { getCachedWorkers } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { hourToTimeStr, timeStrToHour, snapToGrid, GRID_HOUR_START, GRID_HOUR_END } from "@utils/timeGrid";
import { SNAP_HOUR } from "../../atelier/gridTypes";

// Snap step matches the planning atelier granularity.
const SNAP_STEP = SNAP_HOUR;

// "Formation" is an absence like congé (stored zoneType "conge" + absenceKind), only labelled
// differently so the planning atelier says why the compagnon is away (Mélanie, 2026-10-08).
type ZoneChoice = ZoneType | "formation";
const ZONE_OPTIONS: { value: ZoneChoice; label: string }[] = [
  ...ZONE_TYPES.map((z) => ({ value: z as ZoneChoice, label: ZONE_COLORS[z].label })),
  { value: "formation", label: "Formation" },
];

type Period = { from: string; to: string };

const RECURRENCE_OPTIONS: { value: BlockRecurrence; label: string }[] = [
  { value: "none", label: "Ne se répète pas" },
  { value: "weekly", label: "Chaque semaine" },
  { value: "annual", label: "Chaque année" },
];

function dowOf(dateStr: string): number {
  const day = new Date(dateStr + "T00:00:00").getDay();
  return day === 0 ? 6 : day - 1;
}

interface BlockModalProps {
  mode: "edit" | "create";
  block?: AvailabilityBlock;
  workerId?: string;
  selectedWorkerIds?: string[];
  initialDate?: string;
  initialStartHour?: number;
  initialDuration?: number;
  onClose: () => void;
}

export function BlockModal({
  mode,
  block,
  workerId,
  selectedWorkerIds,
  initialDate,
  initialStartHour,
  initialDuration = SNAP_STEP,
  onClose,
}: BlockModalProps) {
  const orgData = useDataStore((s) => s.orgData);
  const HOURS_START = orgData?.config?.booking_opening_hour ?? 8;
  const HOURS_END = Math.max((orgData?.config?.booking_closing_hour ?? 18) + 2, 20);
  const effectiveStartHour = initialStartHour ?? HOURS_START;
  const updateBlock = useDispoAtelierStore((s) => s.updateBlock);
  const removeBlock = useDispoAtelierStore((s) => s.removeBlock);
  const addBlock = useDispoAtelierStore((s) => s.addBlock);

  const isEdit = mode === "edit" && block;

  const [label, setLabel] = useState(isEdit ? block.label : "");
  const [zoneChoice, setZoneChoice] = useState<ZoneChoice>(
    isEdit ? (block.zoneType === "conge" && block.absenceKind === "formation" ? "formation" : block.zoneType || "mecanique") : "mecanique",
  );
  const zoneType: ZoneType = zoneChoice === "formation" ? "conge" : zoneChoice;
  const absenceKind = zoneChoice === "formation" ? ("formation" as const) : undefined;
  // Extra date ranges saved as copies of this créneau (alternants : several school weeks at once).
  const [extraPeriods, setExtraPeriods] = useState<Period[]>([]);
  const [recurrence, setRecurrence] = useState<BlockRecurrence>(isEdit ? getBlockRecurrence(block) : "none");
  const [allDay, setAllDay] = useState(isEdit ? !!block.allDay : false);
  const [startDate, setStartDate] = useState(isEdit ? block.date : initialDate!);
  const [endDate, setEndDate] = useState(isEdit ? block.endDate || "" : "");
  const [startTime, setStartTime] = useState(
    hourToTimeStr(isEdit ? block.startHour : effectiveStartHour),
  );
  const [endTime, setEndTime] = useState(
    hourToTimeStr(
      isEdit
        ? block.endHour ?? block.startHour + block.durationHours
        : effectiveStartHour + initialDuration,
    ),
  );

  const [chosenWorkerId, setChosenWorkerId] = useState(
    isEdit ? block.workerId : (workerId || selectedWorkerIds?.[0] || ""),
  );

  const workerOptions = useMemo(() => {
    const workers = getCachedWorkers();
    if (isEdit) {
      return workers.map((w) => ({ value: w.id, label: w.name }));
    }
    const ids = selectedWorkerIds || (workerId ? [workerId] : []);
    return workers
      .filter((w) => ids.includes(w.id))
      .map((w) => ({ value: w.id, label: w.name }));
  }, [isEdit, selectedWorkerIds, workerId]);

  const showWorkerPicker = workerOptions.length > 1;

  /** Build the block fields shared by create + edit, derived from the form. */
  const buildFields = (startDate: string, endDate: string): Omit<AvailabilityBlock, "id" | "workerId" | "label" | "type"> | null => {
    const effectiveEndDate = endDate && endDate > startDate ? endDate : undefined;
    // Weekly: « Au » ends the recurrence, every occurrence is one single-day slot.
    const multiDay = !!effectiveEndDate && recurrence !== "weekly";

    if (allDay) {
      return {
        date: startDate,
        endDate: effectiveEndDate,
        allDay: true,
        startHour: GRID_HOUR_START,
        durationHours: multiDay ? 0 : GRID_HOUR_END - GRID_HOUR_START,
        endHour: multiDay ? GRID_HOUR_END : undefined,
        recurring: recurrence === "weekly",
        recurrence,
        dayOfWeek: recurrence === "weekly" ? dowOf(startDate) : undefined,
        zoneType,
        absenceKind,
      };
    }

    const start = snapToGrid(timeStrToHour(startTime), SNAP_STEP);
    const end = snapToGrid(timeStrToHour(endTime), SNAP_STEP);
    if (!multiDay && end - start <= 0) return null;
    return {
      date: startDate,
      endDate: effectiveEndDate,
      allDay: false,
      startHour: start,
      durationHours: multiDay ? 0 : end - start,
      endHour: multiDay ? end : undefined,
      recurring: recurrence === "weekly",
      recurrence,
      dayOfWeek: recurrence === "weekly" ? dowOf(startDate) : undefined,
      zoneType,
      absenceKind,
    };
  };

  const handleSave = () => {
    const fields = buildFields(startDate, endDate);
    if (!fields) return;
    const type = zoneType === "conge" ? "conge" : "off";
    if (isEdit) {
      updateBlock(block.id, { ...fields, workerId: chosenWorkerId, label: label.trim(), type });
    } else {
      addBlock({ ...fields, workerId: chosenWorkerId, label: label.trim(), type }, orgData?.id || "");
    }
    for (const p of extraPeriods) {
      const extra = p.from ? buildFields(p.from, p.to) : null;
      if (extra) addBlock({ ...extra, workerId: chosenWorkerId, label: label.trim(), type }, orgData?.id || "");
    }
    onClose();
  };

  /**
   * Replicate the current créneau across every weekday (Mon–Fri) of its start
   * week for this worker, as single-day blocks (multi-day span is dropped here).
   */
  const handleApplyWeek = () => {
    const fields = buildFields(startDate, endDate);
    if (!fields) return;
    const type = zoneType === "conge" ? "conge" : "off";
    const weekDates = getWeekDates(getMonday(startDate)).slice(0, 5); // Mon–Fri
    const orgId = orgData?.id || "";
    const existing = useDispoAtelierStore.getState().blocks;

    if (isEdit) {
      updateBlock(block.id, { ...fields, workerId: chosenWorkerId, label: label.trim(), type });
    }

    weekDates.forEach((d, i) => {
      if (isEdit && d === startDate) return; // current block already handled
      const alreadyThere = existing.some(
        (b) =>
          b.workerId === chosenWorkerId &&
          b.zoneType === zoneType &&
          Math.abs(b.startHour - fields.startHour) < 1e-6 &&
          (b.date === d || (b.recurring && b.dayOfWeek === i)),
      );
      if (alreadyThere) return;
      addBlock(
        {
          ...fields,
          // Single-day per weekday — drop any multi-day span.
          endDate: undefined,
          endHour: undefined,
          date: d,
          dayOfWeek: recurrence === "weekly" ? i : undefined,
          workerId: chosenWorkerId,
          label: label.trim(),
          type,
        },
        orgId,
      );
    });
    onClose();
  };

  const handleDelete = () => {
    if (isEdit) {
      removeBlock(block.id);
      onClose();
    }
  };

  return (
    <BaseModal
      onClose={onClose}
      title={isEdit ? "Modifier le créneau" : "Ajouter un créneau"}
      startIcon={<Icon1 name={isEdit ? "sett-dispo" : "plus"} size={20} />}
      width="max-w-600"
    >
      <div className="flex flex-col p-5" style={{ gap: 16 }}>
        {showWorkerPicker && (
          <DropDown
            label="Mécanicien"
            options={workerOptions}
            value={chosenWorkerId}
            onSelect={(v) => setChosenWorkerId(String(v))}
          />
        )}

        <DropDown
          label="Type de zone"
          options={ZONE_OPTIONS}
          value={zoneChoice}
          onSelect={(v) => setZoneChoice(v as ZoneChoice)}
        />

        <TextField
          label="Commentaires"
          value={label}
          onChange={(v) => setLabel(v)}
          placeholder="Ex: Congés, Mécanique lundi matin…"
        />

        {/* Dates (Google-Calendar style: start + optional end) */}
        <div className="flex flex-row gap-3">
          <div className="flex-1 flex flex-col gap-1">
            <span className="text-h4 font-medium text-neutral-500">Du</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 rounded-lg border border-neutral-200 text-h3 text-black focus:outline-none focus:border-neutral-400"
              style={{ fontFamily: "Poppins" }}
            />
          </div>
          <div className="flex-1 flex flex-col gap-1">
            <span className="text-h4 font-medium text-neutral-500">Au (optionnel)</span>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 rounded-lg border border-neutral-200 text-h3 text-black focus:outline-none focus:border-neutral-400"
              style={{ fontFamily: "Poppins" }}
            />
          </div>
        </div>

        {extraPeriods.map((p, i) => (
          <div key={i} className="flex flex-row gap-3 items-end">
            <div className="flex-1 flex flex-col gap-1">
              <span className="text-h4 font-medium text-neutral-500">Période {i + 2} — du</span>
              <input
                type="date"
                value={p.from}
                onChange={(e) => setExtraPeriods(extraPeriods.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))}
                className="px-3 py-2 rounded-lg border border-neutral-200 text-h3 text-black focus:outline-none focus:border-neutral-400"
                style={{ fontFamily: "Poppins" }}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1">
              <span className="text-h4 font-medium text-neutral-500">Au (optionnel)</span>
              <input
                type="date"
                value={p.to}
                min={p.from || undefined}
                onChange={(e) => setExtraPeriods(extraPeriods.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))}
                className="px-3 py-2 rounded-lg border border-neutral-200 text-h3 text-black focus:outline-none focus:border-neutral-400"
                style={{ fontFamily: "Poppins" }}
              />
            </div>
            <button
              type="button"
              title="Retirer cette période"
              className="mb-2.5 text-neutral-400 hover:text-red-600"
              onClick={() => setExtraPeriods(extraPeriods.filter((_, j) => j !== i))}
            >
              <Icon1 name="trash" size={16} />
            </button>
          </div>
        ))}
        <SecondaryButton
          variant="small"
          label="Ajouter une période"
          onClick={() => setExtraPeriods([...extraPeriods, { from: "", to: "" }])}
        />

        <SwitchBrick
          checked={allDay}
          onChange={setAllDay}
          title="Toute la journée"
          description="Couvre toute l'amplitude horaire"
        />

        {!allDay && (
          <div className="flex flex-row gap-3">
            <div className="flex-1">
              <TimePicker
                label="Début"
                value={startTime}
                onChange={(v) => setStartTime(v)}
                minHour={HOURS_START}
                maxHour={HOURS_END}
                minuteStep={15}
              />
            </div>
            <div className="flex-1">
              <TimePicker
                label={endDate && endDate > startDate && recurrence !== "weekly" ? "Fin (dernier jour)" : "Fin"}
                value={endTime}
                onChange={(v) => setEndTime(v)}
                minHour={HOURS_START}
                maxHour={HOURS_END}
                minuteStep={15}
              />
            </div>
          </div>
        )}

        <DropDown
          label="Récurrence"
          options={RECURRENCE_OPTIONS}
          value={recurrence}
          onSelect={(v) => setRecurrence(v as BlockRecurrence)}
        />

        {/* Replicate this slot across Mon–Fri of the same week for this worker */}
        <button
          type="button"
          onClick={handleApplyWeek}
          className="flex flex-row items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-dashed transition-colors"
          style={{ borderColor: "#C7D2FE", background: "#EEF2FF", color: "#4F46E5", fontFamily: "Poppins" }}
        >
          <Icon1 name="sett-dispo" size={16} color="#4F46E5" />
          <span className="text-h4 font-medium">Appliquer à toute la semaine (lun–ven)</span>
        </button>

        <div
          className={`flex flex-row gap-2 pt-2 ${isEdit ? "justify-between" : "justify-end"}`}
        >
          {isEdit && (
            <SecondaryButton
              label="Supprimer"
              danger
              onClick={handleDelete}
              variant="small"
            />
          )}
          <div className="flex flex-row gap-2">
            <SecondaryButton
              label="Annuler"
              onClick={onClose}
              variant="small"
            />
            <Button
              label={isEdit ? "Enregistrer" : "Ajouter"}
              onClick={handleSave}
              variant="small"
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
}
